// functions/updateOnboarding.js
import { db } from '../firebase';
import { doc, getDoc, writeBatch } from 'firebase/firestore';
import getServerTimestamp from './endpoints/GetServerTimestamp';
import { ONBOARDING_STEPS } from '../contexts/OnboardingSteps';
import TrainingContentContext from '../contexts/TrainingContentContext';
import { sendEmail } from './endpoints/SendEmail'; // Import sendEmail function

export async function updateOnboardingStep({ practitionerId, step, trainingAction, formData }) {
  try {
    const batch = writeBatch(db);
    const timestamp = await getServerTimestamp();

    // Validate inputs
    if (step && !ONBOARDING_STEPS[step]) {
      throw new Error(`Invalid onboarding step: ${step}`);
    }
    if (trainingAction && !TrainingContentContext.some(item => item.action === trainingAction)) {
      throw new Error(`Invalid training action: ${trainingAction}`);
    }
    if (formData && step !== 'form') {
      throw new Error('formData is only valid for the "form" step');
    }
    if (step === 'training' && trainingAction) {
      // Prevent processing step: 'training' with trainingAction; training completion is handled below
      step = null;
    }

    // Get current practitioner data
    const practitionerRef = doc(db, 'practitioners', practitionerId);
    const practitionerSnap = await getDoc(practitionerRef);
    if (!practitionerSnap.exists()) {
      throw new Error(`Practitioner ${practitionerId} not found`);
    }
    const practitionerData = practitionerSnap.data();

    // Get current app.management.practitioners
    const appRef = doc(db, 'app', 'management');
    const appSnap = await getDoc(appRef);
    if (!appSnap.exists()) {
      throw new Error('App management document not found');
    }
    const appData = appSnap.data();
    const practitioners = appData.practitioners || [];
    const practitionerIndex = practitioners.findIndex(p => p.id === practitionerId);
    if (practitionerIndex === -1) {
      throw new Error(`Practitioner ${practitionerId} not found in app.management`);
    }

    // Prepare updates
    const updatedPractitioner = { ...practitionerData };
    const updatedAppPractitioner = { ...practitioners[practitionerIndex] };
    updatedAppPractitioner.onboarding = updatedAppPractitioner.onboarding || [];

    // Flag to determine if we should check for email sending
    let shouldCheckEmail = false;

    // Handle onboarding step update (excluding training, which is handled below)
    if (step) {
      if (!updatedAppPractitioner.onboarding.includes(step)) {
        updatedAppPractitioner.onboarding.push(step);
      }

      if (step === 'form' && formData) {
        updatedPractitioner.onboarding = updatedPractitioner.onboarding || {};
        updatedPractitioner.onboarding.form = {
          ...formData,
          dateCompleted: timestamp,
          dateModified: timestamp,
        };
        batch.update(practitionerRef, {
          'onboarding.form': updatedPractitioner.onboarding.form,
          dateModified: timestamp,
        });
        shouldCheckEmail = true; // Mark for email check when form is completed
      } else {
        const field = step === 'scheduleCall' ? 'dateScheduled' : 'dateCompleted';
        updatedPractitioner.onboarding = updatedPractitioner.onboarding || {};
        updatedPractitioner.onboarding[step] = updatedPractitioner.onboarding[step] || {};
        updatedPractitioner.onboarding[step][field] = timestamp;
        batch.update(practitionerRef, {
          [`onboarding.${step}.${field}`]: timestamp,
          dateModified: timestamp,
        });
      }
    }

    // Handle training updates
    let trainingCompletedCount = updatedAppPractitioner.trainingCompletedCount || 0;
    if (trainingAction) {
      updatedPractitioner.training = updatedPractitioner.training || {};
      updatedPractitioner.training[trainingAction] = { dateCompleted: timestamp };

      // Count completed trainings based on TrainingContentContext actions
      trainingCompletedCount = TrainingContentContext.filter(
        item => updatedPractitioner.training[item.action]?.dateCompleted
      ).length;

      // Check if all trainings are complete
      const totalTrainings = TrainingContentContext.length;
      const allTrainingsComplete = trainingCompletedCount === totalTrainings;

      if (allTrainingsComplete) {
        updatedPractitioner.onboarding = updatedPractitioner.onboarding || {};
        updatedPractitioner.onboarding.training = { dateCompleted: timestamp };
        batch.update(practitionerRef, {
          [`onboarding.training.dateCompleted`]: timestamp,
        });
        if (!updatedAppPractitioner.onboarding.includes('training')) {
          updatedAppPractitioner.onboarding.push('training');
        }
        shouldCheckEmail = true; // Mark for email check when training is completed
      }

      batch.update(practitionerRef, {
        [`training.${trainingAction}`]: { dateCompleted: timestamp },
        dateModified: timestamp,
      });
    }

    // Check if both training and form are completed and send email if appropriate
    if (shouldCheckEmail) {
      const trainingCompleted = updatedAppPractitioner.onboarding.includes('training');
      const formCompleted = updatedAppPractitioner.onboarding.includes('form');

      if (trainingCompleted && formCompleted) {
        const practitionerName = `${practitionerData.firstName} ${practitionerData.lastName}`;
        const emailData = {
          recipientEmail: process.env.REACT_APP_ENVIRONMENT === 'PROD' ? 'hello@trubalancebioenergetics.com' : 'jhatlestad@gmail.com',
          recipientName: 'TruBalance Admin',
          subject: 'Practitioner is Ready to Schedule Onboarding Call',
          templateId: 'schedule-onboarding',
          dynamicTemplateData: {
            practitionerName,
            practitionerEmail: practitionerData.email,
            practitionerPhone: practitionerData.phone,
          },
          emailType: 'schedule_onboarding_call',
          contextData: {
            practitionerId,
          },
          userId: practitionerId,
          isTest: false,
        };

        await sendEmail(emailData);
      }
    }

    // Check if all onboarding steps are complete
    const requiredSteps = [
      'training',
      'form',
      'cardOnFile',
      'scheduleCall',
      'completeCall',
      'returnKit',
      'personalScanReview',
    ];
    const allStepsComplete = requiredSteps.every(s => updatedAppPractitioner.onboarding.includes(s));
    if (allStepsComplete) {
      updatedAppPractitioner.onboardingComplete = true;
    }

    // Update app.management.practitioners
    updatedAppPractitioner.trainingCompletedCount = trainingCompletedCount;
    practitioners[practitionerIndex] = updatedAppPractitioner;
    batch.update(appRef, { practitioners });

    // Update practitioner dateModified
    updatedPractitioner.dateModified = timestamp;

    // Commit batch
    await batch.commit();

    return { success: true, practitioner: updatedPractitioner };
  } catch (error) {
    console.error('Failed to update onboarding step:', error);
    throw new Error(`Failed to update onboarding step: ${error.message}`);
  }
}